import React from "react";
import { Check } from "lucide-react";

const packages = [
    {
        name: "Layout Model",
        price: "From £250",
        tagline: "A clean 3D layout model for understanding the space.",
        best: [
            "Early planning",
            "Venue layouts",
            "Basic spatial understanding",
            "Furniture & feature placement",
            "Internal team discussions",
            "Simple client explanations",
        ],
        includes: [
            "3D spatial model",
            "Key furniture, scenic or production elements",
            "Basic massing and proportions",
            "Layout clarity",
            "Guest flow considerations",
            "Simple visual exports",
        ],
    },
    {
        name: "Concept Visual Pack",
        price: "From £450",
        tagline: "A semi-dressed model and visual pack for pitches and approvals.",
        best: [
            "Pitches",
            "Creative development",
            "Client presentations",
            "Early approvals",
            "Event concepts needing visual support",
        ],
        includes: [
            "Developed 3D model",
            "Styled visual angles",
            "Basic materials and finishes",
            "Branding or scenic direction",
            "Atmosphere references",
            "Presentation-ready exports",
        ],
    },
    {
        name: "Premium Visual Pack",
        price: "From £800",
        tagline: "Polished visuals for higher-value pitches and detailed concepts.",
        best: [
            "Premium private events",
            "Brand activations",
            "Product launches",
            "Scenic environments",
            "Client-facing proposals",
            "Detailed event concepts",
        ],
        includes: [
            "More detailed spatial design",
            "Multiple visual angles",
            "Refined materials & styling",
            "Scenic & branded moments",
            "Lighting and atmosphere direction",
            "Polished presentation visuals",
        ],
    },
];

const addons = [
    { label: "Fly-through video", price: "from £250" },
    { label: "Bespoke 3D models", price: "from £100" },
    { label: "Technical drawing support", price: "by request" },
    { label: "Additional views / angles", price: "by request" },
    { label: "Major design changes", price: "quoted separately" },
    { label: "Branding changes post-visual", price: "quoted separately" },
    { label: "Additional revision rounds", price: "quoted separately" },
];

export default function Services() {
    return (
        <section
            id="services"
            data-testid="services-section"
            className="section-pad"
        >
            <div className="container-edge">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
                    <h2
                        data-testid="services-heading"
                        className="md:col-span-7 font-display text-4xl md:text-5xl lg:text-6xl tracking-tight font-medium leading-[1.02] text-balance"
                    >
                        Visualisation support to suit the{" "}
                        <span className="italic font-normal text-champagne">
                            stage of the project.
                        </span>
                    </h2>
                    <p className="md:col-span-5 text-foreground/70 text-[15px] leading-relaxed">
                        Every project is different. Level of detail depends on brief,
                        deadline, available information and intended use of the
                        visuals. The packages below are simple starting points.
                    </p>
                </div>

                <div className="mt-16 grid grid-cols-1 lg:grid-cols-3 gap-px bg-border items-stretch">
                    {packages.map((p, i) => (
                        <div
                            key={p.name}
                            data-testid={`service-package-${i}`}
                            className="bg-background p-8 md:p-10 flex flex-col h-full"
                        >
                            <p className="overline">Package 0{i + 1}</p>
                            <h3 className="mt-6 font-display text-3xl md:text-[34px] font-medium tracking-tight">
                                {p.name}
                            </h3>
                            <p className="mt-3 text-[14px] text-foreground/70">
                                {p.tagline}
                            </p>
                            <p className="mt-8 font-display text-4xl font-medium tracking-tight">
                                {p.price}
                            </p>

                            <div className="mt-8">
                                <p className="text-[11px] tracking-[0.18em] uppercase text-foreground/50">
                                    Best for
                                </p>
                                <ul className="mt-3 space-y-1.5">
                                    {p.best.map((b) => (
                                        <li key={b} className="text-[14px] flex items-start gap-2">
                                            <span className="mt-2 inline-block w-2 h-px bg-foreground/40 shrink-0" />
                                            <span className="text-foreground/85">{b}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className="mt-8 pt-8 border-t border-border">
                                <p className="text-[11px] tracking-[0.18em] uppercase text-foreground/50">
                                    Can include
                                </p>
                                <ul className="mt-3 space-y-2">
                                    {p.includes.map((b) => (
                                        <li key={b} className="text-[14px] flex items-start gap-2">
                                            <Check
                                                size={14}
                                                strokeWidth={1.5}
                                                className="mt-1 shrink-0 text-foreground"
                                            />
                                            <span className="text-foreground/85">{b}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <a
                                href="#contact"
                                data-testid={`package-cta-${i}`}
                                className="btn-primary w-full mt-auto"
                                style={{ marginTop: "auto" }}
                            >
                                Enquire about this →
                            </a>
                        </div>
                    ))}
                </div>

                {/* Add-ons as a clean list */}
                <div className="mt-20">
                    <div className="flex items-end justify-between">
                        <p className="overline">Add-ons</p>
                        <p className="text-[11px] tracking-[0.18em] uppercase text-foreground/50">
                            Optional extras
                        </p>
                    </div>
                    <ul className="mt-6 border-t border-border">
                        {addons.map((a) => (
                            <li
                                key={a.label}
                                className="flex items-center justify-between py-5 border-b border-border text-[14px]"
                            >
                                <span className="text-foreground/85">{a.label}</span>
                                <span className="text-foreground/55 text-[13px] tracking-wide">
                                    {a.price}
                                </span>
                            </li>
                        ))}
                    </ul>
                    <p className="mt-8 text-[12px] text-foreground/55 max-w-2xl italic">
                        Prices are starting points and may vary depending on
                        complexity, timescale, available information and level of
                        detail required.
                    </p>
                </div>
            </div>
        </section>
    );
}
